import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  MessageSquare, Plus, Search, Phone, Video, Info, Settings, 
  Send, Paperclip, Smile, MoreVertical, Edit3, Trash2, Reply,
  Hash, Users, User, Clock, CheckCircle2, Pin, Archive, X
} from 'lucide-react';
import api, { API_BASE_URL } from '../utils/api';
import RichTextEditor from './RichTextEditor';
import UserProfileModal from './UserProfileModal';

const InternalMessaging = ({ user }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // UI State
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingMessage, setEditingMessage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  
  // Attachment State
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);
  
  // Emoji State
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPickerPosition, setEmojiPickerPosition] = useState({ top: 0, left: 0 });
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  
  // @Mention State
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const isBoardChat = (conversation) => conversation?.type === 'board_chat';
  const isCommitteeChat = (conversation) => conversation?.type === 'committee_chat';
  const isHideableConversation = (conversation) => {
    if (!conversation) return false;
    if (isBoardChat(conversation) || isCommitteeChat(conversation)) return false;
    return conversation.type !== 'team';
  };

  // Auto-resize textarea based on content
  useEffect(() => {
    if (messageInputRef.current) {
      messageInputRef.current.style.height = 'auto';
      messageInputRef.current.style.height = messageInputRef.current.scrollHeight + 'px';
    }
  }, [messageInput]);

  useEffect(() => {
    loadConversations();
    loadTeamMembers();
    markAllMessagesAsRead(); // Mark all messages as read when opening Internal Messaging
    
    // Auto-refresh messages every 5 seconds (placeholder for real-time)
    const interval = setInterval(() => {
      if (selectedConversation) {
        loadMessages(selectedConversation.id, false);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Listen for conversation open events from notifications
  useEffect(() => {
    const handleOpenConversation = (event) => {
      const conversationId = event.detail;
      console.log('🔔 Event: Open conversation', conversationId);
      console.log('Current conversations count:', conversations.length);
      
      // Find and open the conversation
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) {
        console.log('✅ Opening conversation:', conv.name || conv.id);
        setSelectedConversation(conv);
      } else {
        console.log('⚠️ Conversation not found in list, storing as pending');
        // Store for later
        sessionStorage.setItem('pendingConversation', conversationId);
      }
    };
    
    window.addEventListener('openConversation', handleOpenConversation);
    
    return () => {
      window.removeEventListener('openConversation', handleOpenConversation);
    };
  }, [conversations]);

  // Check for pending conversation after conversations load
  useEffect(() => {
    if (conversations.length > 0) {
      const pendingId = sessionStorage.getItem('pendingConversation');
      if (pendingId) {
        console.log('🔍 Checking for pending conversation:', pendingId);
        const conv = conversations.find(c => c.id === pendingId);
        if (conv) {
          console.log('✅ Opening pending conversation:', conv.name || conv.id);
          setSelectedConversation(conv);
          sessionStorage.removeItem('pendingConversation');
        } else {
          console.log('⚠️ Pending conversation still not in list');
        }
      }
    }
  }, [conversations]);

  // Close emoji picker when clicking outside or handle scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    const handleScroll = () => {
      // Recalculate position on scroll
      if (emojiButtonRef.current) {
        const rect = emojiButtonRef.current.getBoundingClientRect();
        const calculatedTop = rect.top - 549;
        setEmojiPickerPosition({
          top: calculatedTop,
          left: rect.left
        });
        
        // Close picker if scrolled too far from original position
        if (calculatedTop < 0) {
          setShowEmojiPicker(false);
        }
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true); // true for capture phase
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom('auto');
  }, [messages]);

  const scrollToBottom = (behavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (!container) return;
    // Defer to ensure layout is settled before measuring scrollHeight
    requestAnimationFrame(() => {
      container.scrollTo({ top: container.scrollHeight, behavior });
    });
  };

  const loadConversations = async () => {
    try {
      const response = await api.get('/conversations');
      setConversations(response.data || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const hideConversation = async (conversation, event) => {
    event.stopPropagation();
    if (!isHideableConversation(conversation)) {
      return;
    }
    try {
      await api.post(`/conversations/${conversation.id}/hide`);
      setConversations(prev => prev.filter(c => c.id !== conversation.id));
      if (selectedConversation?.id === conversation.id) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error hiding conversation:', error);
      alert('Failed to remove conversation. Please try again.');
    }
  };

  const markAllMessagesAsRead = async () => {
    try {
      await api.post('/internal-messages/mark-all-read', {});
      
      // Trigger badge count refresh by dispatching an event
      window.dispatchEvent(new CustomEvent('refreshBadges'));
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await api.get('/team/members');
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId, showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      if (isBoardChat(selectedConversation)) {
        const response = await api.get('/board/chat');
        const chatMessages = (response.data?.messages || []).map((msg) => ({
          id: msg.id,
          sender_id: msg.user_id,
          sender_name: msg.user_name,
          sender_avatar_url: null,
          content: msg.message,
          attachments: msg.attachments || null,
          reply_to_id: msg.reply_to_id,
          is_edited: msg.is_edited,
          created_at: msg.created_at
        }));
        setMessages(chatMessages);
      } else if (isCommitteeChat(selectedConversation)) {
        const committeeId = selectedConversation?.committee_id;
        if (!committeeId) {
          setMessages([]);
          return;
        }
        const response = await api.get(`/governance/committees/${committeeId}/chat`);
        const chatMessages = (response.data?.messages || []).map((msg) => ({
          id: msg.id,
          sender_id: msg.user_id,
          sender_name: msg.user_name,
          sender_avatar_url: null,
          content: msg.message,
          attachments: msg.attachments || null,
          reply_to_id: msg.reply_to_id,
          is_edited: msg.is_edited,
          created_at: msg.created_at
        }));
        setMessages(chatMessages);
      } else {
        const response = await api.get(`/conversations/${conversationId}/messages`);
        setMessages(response.data || []);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const insertEmoji = (emoji) => {
    setMessageInput(prev => prev + emoji);
    setShowEmojiPicker(false);
    // Focus back on input
    if (messageInputRef.current) {
      messageInputRef.current.focus();
    }
  };

  // Common emojis
  const commonEmojis = [
    '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂',
    '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛',
    '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏',
    '😒', '😞', '😔', '😟', '😕', '🙁', '😣', '😖', '😫', '😩',
    '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵',
    '👍', '👎', '👏', '🙌', '👐', '🤝', '🙏', '✌️', '🤞', '🤟',
    '🤘', '👌', '🤌', '👈', '👉', '👆', '👇', '☝️', '✋', '🤚',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
    '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '✨', '⭐',
    '🔥', '💯', '✅', '❌', '⚠️', '🎉', '🎊', '🎈', '🎁', '🏆'
  ];

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return [];

    setUploadingFiles(true);
    try {
      const uploadedUrls = [];
      
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/upload/message-attachment', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        uploadedUrls.push(response.data.url);
      }
      
      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files. Please try again.');
      return [];
    } finally {
      setUploadingFiles(false);
    }
  };

  const sendMessage = async () => {
    if ((!messageInput.trim() && selectedFiles.length === 0) || !selectedConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      // Upload files first if any
      let attachmentUrls = [];
      if (selectedFiles.length > 0) {
        attachmentUrls = await uploadFiles();
        if (attachmentUrls.length === 0 && selectedFiles.length > 0) {
          // Upload failed
          setSendingMessage(false);
          return;
        }
      }

      const messageData = {
        content: messageInput.trim() || '(image)',
        reply_to_id: replyingTo?.id || null,
        attachments: attachmentUrls.length > 0 ? attachmentUrls : null
      };
      let newMessage = null;

      if (isBoardChat(selectedConversation)) {
        const response = await api.post('/board/chat', {
          message: messageData.content,
          reply_to_id: messageData.reply_to_id,
          attachments: attachmentUrls.length > 0 ? attachmentUrls : null
        });
        const msg = response.data?.message;
        if (msg) {
          newMessage = {
            id: msg.id,
            sender_id: msg.user_id,
            sender_name: msg.user_name,
            sender_avatar_url: null,
            content: msg.message,
            attachments: msg.attachments || null,
            reply_to_id: msg.reply_to_id,
            is_edited: msg.is_edited,
            created_at: msg.created_at
          };
        }
      } else if (isCommitteeChat(selectedConversation)) {
        const committeeId = selectedConversation?.committee_id;
        if (!committeeId) {
          throw new Error('Missing committee_id for committee chat');
        }
        const response = await api.post(`/governance/committees/${committeeId}/chat`, {
          message: messageData.content,
          reply_to_id: messageData.reply_to_id,
          attachments: attachmentUrls.length > 0 ? attachmentUrls : null
        });
        const msg = response.data?.message;
        if (msg) {
          newMessage = {
            id: msg.id,
            sender_id: msg.user_id,
            sender_name: msg.user_name,
            sender_avatar_url: null,
            content: msg.message,
            attachments: msg.attachments || null,
            reply_to_id: msg.reply_to_id,
            is_edited: msg.is_edited,
            created_at: msg.created_at
          };
        }
      } else {
        const response = await api.post(`/conversations/${selectedConversation.id}/messages`, messageData);
        newMessage = response.data;
      }

      if (newMessage) {
        setMessages(prev => [...prev, newMessage]);
      }
      setMessageInput('');
      setSelectedFiles([]);
      setReplyingTo(null); // Clear reply state
      scrollToBottom('smooth');
      
      // Update conversation timestamp and preview
      setConversations(prev => 
        prev.map(conv => 
          conv.id === selectedConversation.id 
            ? { 
                ...conv, 
                last_message_at: newMessage?.created_at || conv.last_message_at,
                last_message_content: newMessage?.content || conv.last_message_content
              }
            : conv
        )
      );
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const createDirectMessage = async (memberId) => {
    try {
      const conversationData = {
        type: 'direct',
        participant_ids: [memberId]
      };

      const response = await api.post('/conversations', conversationData);

      // If conversation already exists, reload and find it
      if (response.data.exists) {
        await loadConversations();
        const existingConv = conversations.find(c => c.id === response.data.id);
        if (existingConv) {
          setSelectedConversation(existingConv);
        }
      } else {
        // Add new conversation to list with full participant data
        const newConversation = {
          ...response.data,
          last_message: null,
          last_message_at: null,
          message_count: 0,
          unread_count: 0
        };
        setConversations(prev => [newConversation, ...prev]);
        setSelectedConversation(newConversation);
      }
      
      setShowNewChatModal(false);
    } catch (error) {
      console.error('Error creating direct message:', error);
      alert('Failed to create direct message: ' + (error.response?.data?.detail || error.message));
    }
  };

  const createGroupChat = async (selectedMembers, groupName) => {
    try {
      const conversationData = {
        type: 'group',
        name: groupName,
        participant_ids: selectedMembers
      };

      const response = await api.post('/conversations', conversationData);

      // Reload conversations to get the new one
      await loadConversations();
      setShowNewChatModal(false);
    } catch (error) {
      console.error('Error creating group chat:', error);
      alert('Failed to create group chat: ' + (error.response?.data?.detail || error.message));
    }
  };

  const createTeamChat = async (team) => {
    try {
      const conversationData = {
        type: 'team',
        name: team.name,
        sub_team_id: team.id,
        participant_ids: [] // Will be populated from sub-team members on backend
      };

      const response = await api.post('/conversations', conversationData);

      // Reload conversations to get the new one
      await loadConversations();
      setShowNewChatModal(false);
      
      // If chat already exists, select it
      if (response.data.exists) {
        const conv = conversations.find(c => c.id === response.data.id);
        if (conv) setSelectedConversation(conv);
      }
    } catch (error) {
      console.error('Error creating team chat:', error);
      alert('Failed to create team chat: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (messageDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getConversationName = (conversation) => {
    if (conversation.type === 'board_chat') return 'Board Chat';
    if (conversation.type === 'committee_chat') return conversation.name || 'Committee Chat';
    if (conversation.name) return conversation.name;
    
    if (conversation.type === 'direct') {
      const participants = Array.isArray(conversation.participants) ? conversation.participants : [];
      const otherParticipant = participants.find(p => p && p.id !== user.id);
      return otherParticipant ? otherParticipant.name : 'Unknown User';
    }
    
    const participantCount = Array.isArray(conversation.participants) ? conversation.participants.length : 0;
    return `Group Chat (${participantCount} members)`;
  };

  const getConversationAvatar = (conversation) => {
    if (conversation.type === 'board_chat') {
      return (
        <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white">
          <Users className="w-5 h-5" />
        </div>
      );
    }
    if (conversation.type === 'committee_chat') {
      return (
        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white">
          <Users className="w-5 h-5" />
        </div>
      );
    }
    if (conversation.type === 'direct') {
      const participants = Array.isArray(conversation.participants) ? conversation.participants : [];
      const otherParticipant = participants.find(p => p && p.id !== user.id);
      
      // Debug logging
      console.log('🎨 Avatar Debug:', {
        conversationId: conversation.id,
        userId: user?.id,
        participants: participants,
        otherParticipant: otherParticipant,
        avatarUrl: otherParticipant?.avatar_url
      });
      
      if (otherParticipant) {
        const initials = otherParticipant.name ? 
          otherParticipant.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : 
          'U';
        
        // Check if avatar_url exists and is valid (not null, not empty, not example.com)
        const hasValidAvatar = otherParticipant.avatar_url && 
          otherParticipant.avatar_url.trim() !== '' &&
          !otherParticipant.avatar_url.includes('example.com');
        
        console.log('🎨 Has valid avatar?', hasValidAvatar, otherParticipant.avatar_url);
        
        if (hasValidAvatar) {
          return (
            <img 
              src={otherParticipant.avatar_url} 
              alt={otherParticipant.name}
              className="w-10 h-10 rounded-full object-cover"
              onError={(e) => {
                console.error('🎨 Image failed to load:', otherParticipant.avatar_url);
                // Fallback to initials if image fails to load
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'flex';
                }
              }}
            />
          );
        }
        
        return (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm bg-gradient-to-br from-purple-500 to-pink-500"
          >
            {initials}
          </div>
        );
      }
    }
    
    return (
      <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white">
        {conversation.type === 'group' ? <Users className="w-5 h-5" /> : <Hash className="w-5 h-5" />}
      </div>
    );
  };

  const getUserInfo = (userId) => {
    return teamMembers.find(m => m.id === userId) || {
      name: 'Unknown User',
      avatar_color: '#6B7280',
      initials: 'U'
    };
  };

  // Handle replying to a message
  const handleReplyToMessage = (message) => {
    setReplyingTo(message);
    // Focus on input
    if (messageInputRef.current) {
      messageInputRef.current.focus();
    }
  };

  // Cancel reply
  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Scroll to replied message
  const scrollToMessage = (messageId) => {
    const element = document.getElementById(`message-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight effect
      element.classList.add('bg-yellow-100');
      setTimeout(() => {
        element.classList.remove('bg-yellow-100');
      }, 2000);
    }
  };

  // Handle @mention input
  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setMessageInput(value);
    setCursorPosition(cursorPos);

    // Check for @ mention
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const afterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's a space after @, if so, close mentions
      if (afterAt.includes(' ')) {
        setShowMentions(false);
        setMentionQuery('');
        return;
      }
      
      // Show mention suggestions
      setMentionQuery(afterAt.toLowerCase());
      const filtered = teamMembers.filter(member => 
        member.name.toLowerCase().includes(afterAt.toLowerCase()) ||
        member.email.toLowerCase().includes(afterAt.toLowerCase())
      );
      setMentionSuggestions(filtered);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery('');
    }
  };

  // Insert mention
  const insertMention = (member) => {
    const textBeforeCursor = messageInput.substring(0, cursorPosition);
    const textAfterCursor = messageInput.substring(cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    const newText = 
      messageInput.substring(0, lastAtIndex) + 
      `@${member.name} ` + 
      textAfterCursor;
    
    setMessageInput(newText);
    setShowMentions(false);
    setMentionQuery('');
    
    // Focus back on input
    setTimeout(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }, 0);
  };

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Convert URLs to clickable links and highlight @mentions
  const formatMessageContent = (content, isCurrentUser = false) => {
    if (!content) return '';
    
    // First, escape HTML
    let formatted = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Link color depends on message bubble
    const linkColor = isCurrentUser ? '#ffffff' : '#2563eb'; // white for sent, blue for received
    const linkHoverColor = isCurrentUser ? '#e0e0e0' : '#1e40af';
    
    // Detect and linkify URLs with protocol (http:// or https://)
    const urlWithProtocolRegex = /(https?:\/\/[^\s<]+[^\s<.,;!?'")\]])/gi;
    formatted = formatted.replace(urlWithProtocolRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="message-link" style="color: ${linkColor}; text-decoration: underline; font-weight: 600;">${url}</a>`;
    });
    
    // Detect and linkify domains without protocol (e.g., google.com, www.example.com)
    const domainRegex = /\b(?:www\.)?([a-zA-Z0-9][a-zA-Z0-9-]*\.(?:com|org|net|edu|gov|io|co|us|uk|ca|au|de|fr|jp|cn|in|br|ru|it|es|nl|se|no|pl|be|ch|at|dk|fi|nz|ie|mx|sg|hk|za|kr|tw|th|my|id|ph|vn|ar|cl|pe|ve|co\.uk|co\.nz|co\.za|com\.au|com\.br))\b/gi;
    formatted = formatted.replace(domainRegex, (match) => {
      // Check if already part of a link
      if (formatted.indexOf(`href="${match}"`) !== -1 || formatted.indexOf(`href="http`) !== -1) {
        return match;
      }
      return `<a href="https://${match}" target="_blank" rel="noopener noreferrer" class="message-link" style="color: ${linkColor}; text-decoration: underline; font-weight: 600;">${match}</a>`;
    });
    
    // Highlight @mentions - only match actual team member names
    const mentionNames = (teamMembers || [])
      .map(member => member?.name)
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp);
    const mentionRegex = mentionNames.length
      ? new RegExp(`@(?:${mentionNames.join('|')})(?=$|[\\s.,!?])`, 'g')
      : /@([\\w.]+)/g;
    if (isCurrentUser) {
      // White background with dark text for sent messages
      formatted = formatted.replace(mentionRegex, (match) => {
        return `<span style="background-color: rgba(255, 255, 255, 0.3); color: #ffffff; padding: 2px 4px; border-radius: 4px; font-weight: 600;">${match}</span>`;
      });
    } else {
      // Blue background for received messages
      formatted = formatted.replace(mentionRegex, (match) => {
        return `<span style="background-color: #dbeafe; color: #1e40af; padding: 2px 4px; border-radius: 4px; font-weight: 500;">${match}</span>`;
      });
    }
    
    return formatted;
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const name = getConversationName(conv).toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  if (loading && conversations.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 h-full overflow-hidden bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center space-x-3">
          <MessageSquare className="w-8 h-8 text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-900">Internal Messages</h1>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-80 border-r border-gray-200 flex flex-col bg-gray-50">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
            <button
              onClick={() => setShowNewChatModal(true)}
              className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              title="New conversation"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="p-6 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No conversations yet</p>
              <button
                onClick={() => setShowNewChatModal(true)}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium"
              >
                Start your first conversation
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredConversations.map((conversation) => (
                <button
                  key={conversation.id}
                  onClick={() => setSelectedConversation(conversation)}
                  className={`w-full p-4 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-left ${
                    selectedConversation?.id === conversation.id ? 'bg-purple-50 border-r-2 border-purple-600' : ''
                  }`}
                >
                  {getConversationAvatar(conversation)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-gray-900 truncate">
                        {getConversationName(conversation)}
                      </h3>
                      <div className="flex items-center space-x-2">
                        {conversation.last_message_at && (
                          <span className="text-xs text-gray-500">
                            {formatTime(conversation.last_message_at)}
                          </span>
                        )}
                        {isHideableConversation(conversation) && (
                          <button
                            type="button"
                            onClick={(event) => hideConversation(conversation, event)}
                            className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-red-50"
                            title="Remove conversation"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {conversation.last_message_content ? 
                        conversation.last_message_content : 
                        conversation.type === 'direct' ? 'No messages yet' : 
                        conversation.type === 'group' ? `${conversation.participants.length} members` : 
                        conversation.type === 'board_chat' ? 'Board members' :
                        conversation.type === 'committee_chat' ? 'Committee members' :
                        'Channel'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getConversationAvatar(selectedConversation)}
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {getConversationName(selectedConversation)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {selectedConversation.type === 'direct' ? 'Direct message' : 
                     selectedConversation.type === 'board_chat' ? 'Board members' :
                     selectedConversation.type === 'committee_chat' ? 'Committee members' :
                     `${selectedConversation.participants.length} members`}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => alert('Voice calling feature coming soon!')}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  title="Start voice call (coming soon)"
                >
                  <Phone className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => alert('Video calling feature coming soon!')}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  title="Start video call (coming soon)"
                >
                  <Video className="w-4 h-4" />
                </button>
                <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                  <Info className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto overscroll-contain min-h-0 p-4 space-y-4">
              {messages.map((message, index) => {
                const sender = getUserInfo(message.sender_id);
                const currentUserId = String(user?.id || user?.user_id || '');
                const messageSenderId = String(message?.sender_id || '');
                const isCurrentUser = !!currentUserId && messageSenderId === currentUserId;
                const showAvatar = index === 0 || String(messages[index - 1]?.sender_id || '') !== messageSenderId;

                return (
                  <div
                    key={message.id}
                    className={`flex items-start space-x-3 ${isCurrentUser ? 'justify-end' : ''}`}
                  >
                    {!isCurrentUser && (
                      <div className="flex-shrink-0">
                        {showAvatar ? (
                          <button
                            onClick={() => {
                              setSelectedUser(sender);
                              setShowUserProfile(true);
                            }}
                            className="hover:opacity-80 transition-opacity"
                          >
                            {message.sender_avatar_url && !message.sender_avatar_url.includes('example.com') ? (
                              <img 
                                src={message.sender_avatar_url} 
                                alt={message.sender_name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                                style={{ backgroundColor: sender.avatar_color || '#6B7280' }}
                              >
                                {sender.initials || message.sender_name?.substring(0, 2).toUpperCase() || 'U'}
                              </div>
                            )}
                          </button>
                        ) : (
                          <div className="w-8 h-8" />
                        )}
                      </div>
                    )}

                    <div className={`flex-1 max-w-md ${isCurrentUser ? 'order-first' : ''}`}>
                      {showAvatar && !isCurrentUser && (
                        <div className="mb-1">
                          <span className="text-sm font-medium text-gray-900">{sender.name}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            {formatTime(message.created_at)}
                          </span>
                        </div>
                      )}

                      <div className="group relative">
                        <div
                          id={`message-${message.id}`}
                          className={`rounded-lg px-4 py-2 ${
                            isCurrentUser
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          {/* Show replied message context */}
                          {message.reply_to_id && (() => {
                            const repliedMsg = messages.find(m => m.id === message.reply_to_id);
                            if (repliedMsg) {
                              return (
                                <div
                                  onClick={() => scrollToMessage(message.reply_to_id)}
                                  className={`mb-2 pb-2 border-b ${
                                    isCurrentUser ? 'border-purple-400' : 'border-gray-300'
                                  } cursor-pointer hover:opacity-80`}
                                >
                                  <div className={`text-xs ${isCurrentUser ? 'text-purple-200' : 'text-gray-500'} mb-1`}>
                                    Replying to {String(repliedMsg?.sender_id || '') === String(user?.id || user?.user_id || '') ? 'yourself' : repliedMsg.sender_name}
                                  </div>
                                  <div className={`text-sm ${isCurrentUser ? 'text-purple-100' : 'text-gray-600'} line-clamp-2`}>
                                    {repliedMsg.content.substring(0, 100)}{repliedMsg.content.length > 100 ? '...' : ''}
                                  </div>
                                </div>
                              );
                            }
                          })()}
                          
                          <div
                            dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content, isCurrentUser) }}
                            className="whitespace-pre-wrap break-words"
                          />
                          
                          {/* Attachments */}
                          {message.attachments && (() => {
                            // Parse attachments if it's a string
                            let attachments = message.attachments;
                            if (typeof attachments === 'string') {
                              try {
                                attachments = JSON.parse(attachments);
                              } catch (e) {
                                console.error('Failed to parse attachments:', e);
                                return null;
                              }
                            }
                            
                            // Ensure it's an array
                            if (!Array.isArray(attachments) || attachments.length === 0) {
                              return null;
                            }
                            
                            return (
                              <div className="mt-2 space-y-2">
                                {attachments.map((attachment, index) => {
                                const url = typeof attachment === 'string'
                                  ? attachment
                                  : attachment.url || attachment.file_url || attachment.proxy_url || attachment.download_url || '';
                                if (!url) {
                                  const name = attachment.filename || attachment.file_name || attachment.name || 'attachment';
                                  return (
                                    <div key={index} className={`text-sm ${isCurrentUser ? 'text-purple-100' : 'text-gray-600'}`}>
                                      {name}
                                    </div>
                                  );
                                }
                                // Handle GCS URLs with query params - check before the ? or at end
                                const urlPath = url.split('?')[0];
                                const isImage = urlPath.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                const isVideo = urlPath.match(/\.(mp4|webm|ogg|mov)$/i);
                                const fileName = (attachment.filename || attachment.file_name || attachment.name || urlPath.split('/').pop() || 'attachment');
                                
                                if (isImage) {
                                  return (
                                    <div key={index} className="max-w-xs">
                                      <img 
                                        src={url} 
                                        alt="Attachment"
                                        className="rounded-lg max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => window.open(url, '_blank')}
                                        onError={(e) => {
                                          // Fallback to link if image fails to load
                                          e.target.style.display = 'none';
                                          e.target.nextSibling?.classList.remove('hidden');
                                        }}
                                      />
                                      <a 
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hidden text-sm text-blue-600 hover:underline"
                                      >
                                        {fileName}
                                      </a>
                                    </div>
                                  );
                                } else if (isVideo) {
                                  return (
                                    <div key={index} className="max-w-xs">
                                      <video 
                                        src={url}
                                        controls
                                        className="rounded-lg max-w-full h-auto"
                                      />
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div key={index} className={`flex items-center space-x-2 p-2 rounded border ${
                                      isCurrentUser ? 'border-purple-400 bg-purple-500' : 'border-gray-300 bg-white'
                                    }`}>
                                      <Paperclip className={`w-4 h-4 ${isCurrentUser ? 'text-purple-200' : 'text-gray-500'}`} />
                                      <a 
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`text-sm hover:underline ${
                                          isCurrentUser ? 'text-purple-100' : 'text-blue-600'
                                        }`}
                                      >
                                        {fileName}
                                      </a>
                                    </div>
                                  );
                                }
                              })}
                              </div>
                            );
                          })()}
                          
                          {message.is_edited && (
                            <span className={`text-xs ${isCurrentUser ? 'text-purple-200' : 'text-gray-500'} mt-1 block`}>
                              (edited)
                            </span>
                          )}
                        </div>
                        
                        {/* Reply button (shows on hover) */}
                        <button
                          onClick={() => handleReplyToMessage(message)}
                          className="absolute -top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-gray-300 rounded-full p-1 shadow-sm hover:bg-gray-50"
                          title="Reply to this message"
                        >
                          <Reply className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>

                      {isCurrentUser && showAvatar && (
                        <div className="text-right mt-1">
                          <span className="text-xs text-gray-500">
                            {formatTime(message.created_at)}
                          </span>
                        </div>
                      )}
                    </div>

                    {isCurrentUser && (
                      <div className="flex-shrink-0">
                        {showAvatar ? (
                          <div className="hover:opacity-80 transition-opacity">
                            {user.avatar_url && !user.avatar_url.includes('example.com') ? (
                              <img 
                                src={user.avatar_url} 
                                alt={user.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                                style={{ backgroundColor: user.avatar_color || '#6B46C1' }}
                              >
                                {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="w-8 h-8" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-end space-x-2">
                <div className="flex-1 relative">
                  {/* Reply Preview Bar */}
                  {replyingTo && (
                    <div className="mb-2 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <Reply className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-900">
                            Replying to {String(replyingTo?.sender_id || '') === String(user?.id || user?.user_id || '') ? 'yourself' : replyingTo.sender_name}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 line-clamp-2">
                          {replyingTo.content.substring(0, 100)}{replyingTo.content.length > 100 ? '...' : ''}
                        </div>
                      </div>
                      <button
                        onClick={cancelReply}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                  
                  {/* @Mention Suggestions Dropdown */}
                  {showMentions && mentionSuggestions.length > 0 && (
                    <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                      {mentionSuggestions.map(member => (
                        <div
                          key={member.id}
                          onClick={() => insertMention(member)}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center space-x-3"
                        >
                          {member.avatar_url ? (
                            <img src={member.avatar_url} alt={member.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-medium">
                              {member.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-gray-900">{member.name}</div>
                            <div className="text-xs text-gray-500">{member.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* File Preview Area */}
                  {selectedFiles.length > 0 && (
                    <div className="mb-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                        </span>
                        <button
                          onClick={() => setSelectedFiles([])}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-2">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-white rounded p-2 border">
                            <div className="flex items-center space-x-2">
                              <Paperclip className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700 truncate">{file.name}</span>
                              <span className="text-xs text-gray-500">
                                ({(file.size / 1024 / 1024).toFixed(1)} MB)
                              </span>
                            </div>
                            <button
                              onClick={() => removeFile(index)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <textarea
                      ref={messageInputRef}
                      value={messageInput}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder={`Message ${getConversationName(selectedConversation)}... (Type @ to mention)`}
                      className="w-full px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset"
                      style={{ minHeight: '44px', maxHeight: '200px', overflowY: 'auto' }}
                      rows={1}
                      style={{
                        minHeight: '44px',
                        maxHeight: '120px',
                        overflow: 'auto'
                      }}
                    />
                    
                    <div className="px-4 pb-3 flex items-center justify-between" style={{ overflow: 'visible' }}>
                      <div className="flex items-center space-x-2 relative" style={{ overflow: 'visible' }}>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                        />
                        <button 
                          onClick={() => fileInputRef.current?.click()}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          disabled={uploadingFiles}
                          title="Attach file"
                        >
                          <Paperclip className="w-4 h-4" />
                        </button>
                        <button 
                          ref={emojiButtonRef}
                          onClick={(e) => {
                            if (!showEmojiPicker && emojiButtonRef.current) {
                              const rect = emojiButtonRef.current.getBoundingClientRect();
                              console.log('Button position:', rect.top, rect.left);
                              // Position picker higher - need bottom at red line
                              // Button at 819px, want picker bottom at ~550px (270px above button)
                              // Picker is ~280px tall, so top should be at 550 - 280 = 270px
                              // That means: 819 - 270 = 549px offset needed
                              const calculatedTop = rect.top - 549;
                              console.log('Setting picker top to:', calculatedTop);
                              setEmojiPickerPosition({
                                top: calculatedTop,
                                left: rect.left
                              });
                            }
                            setShowEmojiPicker(!showEmojiPicker);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          title="Insert emoji"
                        >
                          <Smile className="w-4 h-4" />
                        </button>
                        
                        {/* Emoji Picker rendered via Portal */}
                        {showEmojiPicker && ReactDOM.createPortal(
                          <div 
                            ref={emojiPickerRef}
                            className="fixed bg-white border border-gray-300 rounded-lg shadow-2xl p-4"
                            style={{ 
                              top: `${emojiPickerPosition.top}px`,
                              left: `${emojiPickerPosition.left}px`,
                              zIndex: 99999,
                              width: '432px'
                            }}
                          >
                            <div className="grid grid-cols-12 gap-1">
                              {commonEmojis.map((emoji, index) => (
                                <button
                                  key={index}
                                  onClick={() => insertEmoji(emoji)}
                                  className="text-2xl hover:bg-gray-100 rounded p-1.5 transition-colors"
                                  title={emoji}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>
                      
                      <button
                        onClick={sendMessage}
                        disabled={(!messageInput.trim() && selectedFiles.length === 0) || sendingMessage || uploadingFiles}
                        className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Send className="w-4 h-4" />
                        <span>
                          {uploadingFiles ? 'Uploading...' : sendingMessage ? 'Sending...' : 'Send'}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Select a conversation
              </h3>
              <p className="text-gray-600 mb-4">
                Choose a conversation from the sidebar to start messaging
              </p>
              <button
                onClick={() => setShowNewChatModal(true)}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Start New Conversation
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* New Chat Modal */}
      <NewChatModal
        isOpen={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        teamMembers={teamMembers}
        currentUserId={user.id}
        onCreateDirectMessage={createDirectMessage}
        onCreateGroupChat={createGroupChat}
        onCreateTeamChat={createTeamChat}
      />

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
        userId={selectedUser?.id}
        userName={selectedUser?.name}
      />
    </div>
  );
};

const getMemberInitials = (member) => {
  const name = member?.name?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  const email = member?.email?.trim();
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  return 'U';
};

// New Chat Modal Component
const NewChatModal = ({ 
  isOpen, 
  onClose, 
  teamMembers, 
  currentUserId, 
  onCreateDirectMessage, 
  onCreateGroupChat,
  onCreateTeamChat
}) => {
  const [chatType, setChatType] = useState('direct'); // 'direct', 'group', or 'team'
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  
  // Team chat state
  const [availableTeams, setAvailableTeams] = useState([]);
  const [teamsEnabled, setTeamsEnabled] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Fetch available teams when modal opens
  useEffect(() => {
    if (isOpen) {
      setChatType('direct');
      setSelectedMembers([]);
      setGroupName('');
      setSearchQuery('');
      setCreating(false);
      setError('');
      setSelectedTeam(null);
      
      // Fetch available team chats
      const fetchTeams = async () => {
        setLoadingTeams(true);
        try {
          const response = await api.get('/conversations/available-team-chats');
          setAvailableTeams(response.data.sub_teams || []);
          setTeamsEnabled(response.data.enabled || false);
        } catch (err) {
          console.error('Failed to fetch available teams:', err);
          setTeamsEnabled(false);
        } finally {
          setLoadingTeams(false);
        }
      };
      fetchTeams();
    }
  }, [isOpen]);

  const filteredMembers = teamMembers.filter(member => 
    member.id !== currentUserId &&
    member.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleMemberToggle = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleCreate = async () => {
    if (creating) return;
    
    setCreating(true);
    setError('');
    
    try {
      if (chatType === 'direct' && selectedMembers.length === 1) {
        await onCreateDirectMessage(selectedMembers[0]);
      } else if (chatType === 'group' && selectedMembers.length > 0) {
        await onCreateGroupChat(selectedMembers, groupName || 'New Group Chat');
      } else if (chatType === 'team' && selectedTeam) {
        await onCreateTeamChat(selectedTeam);
      }
      // Modal will be closed by the parent component after successful creation
    } catch (error) {
      console.error('Error creating conversation:', error);
      setError('Failed to create conversation. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const canCreate = chatType === 'direct' 
    ? selectedMembers.length === 1 
    : chatType === 'team' 
      ? selectedTeam !== null 
      : selectedMembers.length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">New Conversation</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Chat Type Selection */}
          <div className="mb-6">
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setChatType('direct')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  chatType === 'direct'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <User className="w-4 h-4 inline mr-1" />
                Direct
              </button>
              <button
                onClick={() => setChatType('group')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  chatType === 'group'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Users className="w-4 h-4 inline mr-1" />
                Group
              </button>
              {teamsEnabled && (
                <button
                  onClick={() => setChatType('team')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    chatType === 'team'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Hash className="w-4 h-4 inline mr-1" />
                  Team
                </button>
              )}
            </div>
          </div>

          {/* Team Selection (for Team Chat) */}
          {chatType === 'team' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Team
              </label>
              {loadingTeams ? (
                <div className="text-center py-4 text-gray-500">Loading teams...</div>
              ) : availableTeams.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No teams available. Create teams in Settings first.
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availableTeams.map(team => (
                    <button
                      key={team.id}
                      onClick={() => setSelectedTeam(team)}
                      disabled={team.has_chat}
                      className={`w-full p-3 flex items-center space-x-3 rounded-lg border transition-colors ${
                        team.has_chat
                          ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                          : selectedTeam?.id === team.id
                            ? 'border-purple-300 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${team.color}20` }}
                      >
                        <Hash className="w-4 h-4" style={{ color: team.color }} />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-gray-900">{team.name}</div>
                        {team.has_chat && (
                          <div className="text-xs text-gray-500">Chat already exists</div>
                        )}
                      </div>
                      {selectedTeam?.id === team.id && !team.has_chat && (
                        <CheckCircle2 className="w-5 h-5 text-purple-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Group Name Input */}
          {chatType === 'group' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group Name
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter group name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Member Search - Only for direct and group */}
          {chatType !== 'team' && (
            <>
              <div className="mb-4">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search team members..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Members List */}
              <div className="mb-6 max-h-64 overflow-y-auto">
                <div className="space-y-2">
                  {filteredMembers.map((member) => {
                    const hasAvatar = member?.avatar_url &&
                      member.avatar_url.trim() !== '' &&
                      !member.avatar_url.includes('example.com');

                    return (
                    <button
                      key={member.id}
                      onClick={() => handleMemberToggle(member.id)}
                      className={`w-full p-3 flex items-center space-x-3 rounded-lg border transition-colors ${
                        selectedMembers.includes(member.id)
                          ? 'border-purple-300 bg-purple-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {hasAvatar ? (
                        <img
                          src={member.avatar_url}
                          alt={member.name || 'User avatar'}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-sm">
                          {getMemberInitials(member)}
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <div className="font-medium text-gray-900">{member.name}</div>
                        <div className="text-sm text-gray-600">{member.email}</div>
                      </div>
                      {selectedMembers.includes(member.id) && (
                        <CheckCircle2 className="w-5 h-5 text-purple-600" />
                      )}
                    </button>
                  );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={creating}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!canCreate || creating}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {creating ? 'Creating...' : (chatType === 'direct' ? 'Start Chat' : 'Create Group')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalMessaging;
